import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Flame, Sparkles, Trophy } from 'lucide-react';
import { getArcadePersonalBests, type ArcadePersonalBest } from '../api/learning';
import { groupPersonalBests, humanizeToken } from './arcadePersonalBestsUtils';
import './ArcadePersonalBests.css';

function formatAchievedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Recently';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; items: ArcadePersonalBest[]; streakDays: number };

export function ArcadePersonalBestsPanel() {
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  const load = useCallback(() => {
    setState({ status: 'loading' });
    getArcadePersonalBests()
      .then(response => {
        setState({
          status: 'ready',
          items: response.items,
          streakDays: response.streakDays,
        });
      })
      .catch(() => {
        // Generic client message only — no payload/content in logs (SECURITY.md).
        setState({
          status: 'error',
          message: 'We could not load your personal bests. Try again.',
        });
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(
    () => (state.status === 'ready' ? groupPersonalBests(state.items) : []),
    [state]
  );

  return (
    <section
      className="arcade-personal-bests"
      aria-labelledby="arcade-personal-bests-heading"
      data-testid="arcade-personal-bests"
    >
      <div className="arcade-personal-bests__header">
        <div>
          <h2 id="arcade-personal-bests-heading">
            <Trophy size={20} aria-hidden="true" /> Your personal bests
          </h2>
          <p>Self-comparison only — scores from your own arcade runs.</p>
        </div>
        {state.status === 'ready' && state.streakDays > 0 && (
          <span
            className="arcade-personal-bests__streak"
            data-testid="arcade-personal-bests-streak"
            aria-label={`${state.streakDays} day learning streak`}
          >
            <Flame size={16} aria-hidden="true" />
            <span>
              {state.streakDays} day{state.streakDays === 1 ? '' : 's'} streak
            </span>
          </span>
        )}
      </div>

      {state.status === 'loading' && (
        <p className="arcade-personal-bests__status" role="status" aria-live="polite">
          Loading your personal bests…
        </p>
      )}

      {state.status === 'error' && (
        <div
          className="arcade-personal-bests__error"
          role="alert"
          data-testid="arcade-personal-bests-error"
        >
          <AlertCircle size={18} aria-hidden="true" />
          <span>{state.message}</span>
          <button type="button" onClick={load} className="arcade-personal-bests__retry">
            Try again
          </button>
        </div>
      )}

      {state.status === 'ready' && groups.length === 0 && (
        <div className="arcade-personal-bests__empty" data-testid="arcade-personal-bests-empty">
          <Sparkles size={28} aria-hidden="true" />
          <h3>No personal bests yet</h3>
          <p>Play any arcade game — your best score per mode and timing will show up here.</p>
        </div>
      )}

      {state.status === 'ready' && groups.length > 0 && (
        <ul className="arcade-personal-bests__modes" data-testid="arcade-personal-bests-list">
          {groups.map(group => (
            <li key={group.mode} className="arcade-personal-bests__mode-card">
              <h3 className="arcade-personal-bests__mode-title">{humanizeToken(group.mode)}</h3>
              <ul className="arcade-personal-bests__rows">
                {group.rows.map(row => (
                  <li
                    key={`${row.mode}-${row.timing}-${row.achievedAt}`}
                    className="arcade-personal-bests__row"
                    data-testid="arcade-personal-best-row"
                    data-mode={row.mode}
                    data-timing={row.timing}
                  >
                    <span className="arcade-personal-bests__timing">
                      {humanizeToken(row.timing)}
                    </span>
                    <span className="arcade-personal-bests__score">
                      <span className="arcade-personal-bests__score-value">
                        {row.bestScore.toLocaleString()}
                      </span>
                      <span className="arcade-personal-bests__score-label">points</span>
                    </span>
                    <span className="arcade-personal-bests__meta">
                      Combo {row.bestCombo.toLocaleString()} · {formatAchievedAt(row.achievedAt)}
                    </span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
